import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../config";
import "./SinglePro.css";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [isAdded, setIsAdded] = useState(false);

  // ==========================================
  // SIZE CHART
  // ==========================================

  const [showSizeChart, setShowSizeChart] = useState(false);

  // ==========================================
  // DETAILS
  // ==========================================

  const [showDetails, setShowDetails] = useState(false);

  // ==========================================
  // ACTIVE IMAGE
  // ==========================================

  const [activeImg, setActiveImg] = useState("");

  // ==========================================
  // ACTIVE IMAGE INDEX
  // ==========================================

  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // ==========================================
  // FETCH PRODUCT
  // ==========================================

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);

      console.log("Fetching Product ID:", id);

      const response = await axios.get(`${API_BASE}/products/${id}`);

      console.log("Product Detail API Response:", response.data);

      const data = response.data;

      setProduct(data);

      // ==========================================
      // SAVE LAST PRODUCT ID
      // ==========================================

      if (data?.id) {
        sessionStorage.setItem("lastProductId", String(data.id));

        console.log("Last Product ID Saved:", data.id);
      }

      // ==========================================
      // SET FIRST / MAIN IMAGE
      // ==========================================

      if (Array.isArray(data.images) && data.images.length > 0) {
        const mainIndex = data.images.findIndex((img) => img?.isMain === true);

        const imageIndex = mainIndex >= 0 ? mainIndex : 0;

        const mainImage = data.images[imageIndex]?.imageUrl || "";

        setActiveImageIndex(imageIndex);
        setActiveImg(mainImage);
      }
    } catch (error) {
      console.error("Error fetching product:", error);

      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // AUTO IMAGE SLIDER
  // ==========================================

  useEffect(() => {
    if (!product?.images || product.images.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setActiveImageIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % product.images.length;

        const nextImage = product.images[nextIndex]?.imageUrl || "";

        setActiveImg(nextImage);

        return nextIndex;
      });
    }, 2500);

    return () => {
      clearInterval(interval);
    };
  }, [product]);

  // ==========================================
  // QUANTITY
  // ==========================================

  const increaseQty = () => {
    setQty((prev) => prev + 1);
  };

  const decreaseQty = () => {
    setQty((prev) => {
      if (prev > 1) {
        return prev - 1;
      }

      return 1;
    });
  };

  // ==========================================
  // ADD TO CART
  // ==========================================

  const handleAddToCart = async () => {
    try {
      const token = sessionStorage.getItem("token");

      // ==========================================
      // LOGIN NOT FOUND
      // ==========================================

      if (!token) {
        sessionStorage.setItem("loginRedirect", `/product/${id}`);

        window.dispatchEvent(new Event("openLoginPopup"));

        return;
      }

      // ==========================================
      // PRODUCT ALREADY ADDED
      // ==========================================

      if (isAdded) {
        navigate("/cart");
        return;
      }

      // ==========================================
      // ADD PRODUCT TO CART
      // ==========================================

      await axios.post(
        `${API_BASE}/cart/add`,
        {
          productId: product.id,
          quantity: qty,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("Product added to cart successfully");

      setIsAdded(true);
    } catch (error) {
      console.error("Add to cart error:", error);

      // ==========================================
      // TOKEN EXPIRED / UNAUTHORIZED
      // ==========================================

      if (error?.response?.status === 401 || error?.response?.status === 403) {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("customer");
        sessionStorage.removeItem("tokenExpiry");

        window.dispatchEvent(new Event("openLoginPopup"));

        return;
      }

      alert(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to add to cart",
      );
    }
  };

  // ==========================================
  // LOADING
  // ==========================================

  if (loading) {
    return <h2 className="sing-loading">Loading...</h2>;
  }

  // ==========================================
  // PRODUCT NOT FOUND
  // ==========================================

  if (!product) {
    return <h2 className="sing-loading">Product not found</h2>;
  }

  // ==========================================
  // PRODUCT IMAGES
  // ==========================================

  const productImages = Array.isArray(product.images) ? product.images : [];

  // ==========================================
  // PRODUCT NAME
  // ==========================================

  const productName =
    typeof product.name === "object"
      ? product.name?.name || product.name?.title || "Product"
      : product.name || "Product";

  // ==========================================
  // CATEGORY
  // ==========================================

  const category =
    typeof product.category === "object"
      ? product.category?.name || product.category?.title || "-"
      : product.category || "-";

  // ==========================================
  // NORMALIZED CATEGORY
  // ==========================================

  const normalizedCategory = String(category).trim().toLowerCase();

  // ==========================================
  // SIZE ELIGIBLE CATEGORY
  // ONLY KURTI / DRESS / TOP / TOPS
  // ==========================================

  const isSizeChartAvailable = ["kurti", "dress", "top", "tops"].includes(
    normalizedCategory,
  );

  // ==========================================
  // HARD-CODED SIZES
  // ==========================================

  const availableSizes = ["XS", "S", "M", "L", "XL", "XXL"];

  // ==========================================
  // SIZE CHART DATA
  // ==========================================

  const sizeChartData = [
    {
      size: "XS",
      bust: "32",
      waist: "26",
      hip: "34",
      length: "44",
    },
    {
      size: "S",
      bust: "34",
      waist: "28",
      hip: "36",
      length: "44",
    },
    {
      size: "M",
      bust: "36",
      waist: "30",
      hip: "38",
      length: "45",
    },
    {
      size: "L",
      bust: "38",
      waist: "32",
      hip: "40",
      length: "45",
    },
    {
      size: "XL",
      bust: "40",
      waist: "34",
      hip: "42",
      length: "46",
    },
    {
      size: "XXL",
      bust: "42",
      waist: "36",
      hip: "44",
      length: "46",
    },
  ];

  return (
    <div className="sing-product-container">
      {/* ================================= */}
      {/* LEFT SECTION */}
      {/* ================================= */}

      <div className="sing-left-section">
        {/* THUMBNAILS */}

        <div className="sing-thumbnails">
          {productImages.map((img, index) => {
            const imageUrl = img?.imageUrl || "";

            return (
              <img
                key={img?.id || index}
                src={imageUrl}
                alt={`${productName} thumbnail`}
                className={activeImageIndex === index ? "active-thumb" : ""}
                onClick={() => {
                  setActiveImageIndex(index);
                  setActiveImg(imageUrl);
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            );
          })}
        </div>

        {/* MAIN IMAGE */}

        <div className="sing-main-image">
          {activeImg ? (
            <img
              key={activeImg}
              src={activeImg}
              alt={productName}
              className="sing-main-image-animation"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="sing-no-image">No Image Available</div>
          )}
        </div>
      </div>

      {/* ================================= */}
      {/* RIGHT SECTION */}
      {/* ================================= */}

      <div className="sing-right-section">
        {/* PRODUCT NAME */}

        <h1>{productName}</h1>

        {/* PRICE */}

        <div className="sing-price">
          <span className="sing-new-price">Rs {product.finalPrice ?? 0}</span>

          {Number(product.originalPrice) > Number(product.finalPrice) && (
            <span className="sing-old-price">Rs {product.originalPrice}</span>
          )}
        </div>

        {/* DESCRIPTION */}

        {product.description && (
          <p className="sing-description">{product.description}</p>
        )}

        {/* ==========================================
            SIZE
        ========================================== */}

        {isSizeChartAvailable && (
          <div className="sing-size-section">
            <div className="sing-size-heading-row">
              <p className="sing-size-title">Size</p>

              <button
                type="button"
                className="sing-size-chart-button"
                onClick={() => setShowSizeChart(true)}
              >
                Size Chart
              </button>
            </div>

            <div className="sing-sizes">
              {availableSizes.map((size) => (
                <span
                  key={size}
                  className={`sing-size ${
                    selectedSize === size ? "active" : ""
                  }`}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ==========================================
            SIZE CHART POPUP
        ========================================== */}

        {showSizeChart && isSizeChartAvailable && (
          <div
            className="sing-size-chart-overlay"
            onClick={() => setShowSizeChart(false)}
          >
            <div
              className="sing-size-chart-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sing-size-chart-header">
                <h2>Size Chart</h2>

                <button
                  type="button"
                  className="sing-size-chart-close"
                  onClick={() => setShowSizeChart(false)}
                  aria-label="Close size chart"
                >
                  ×
                </button>
              </div>

              <p className="sing-size-chart-note">
                All measurements are in inches.
              </p>

              <div className="sing-size-chart-table-wrapper">
                <table className="sing-size-chart-table">
                  <thead>
                    <tr>
                      <th>Size</th>
                      <th>Bust</th>
                      <th>Waist</th>
                      <th>Hip</th>
                      <th>Length</th>
                    </tr>
                  </thead>

                  <tbody>
                    {sizeChartData.map((item) => (
                      <tr key={item.size}>
                        <td>{item.size}</td>
                        <td>{item.bust}</td>
                        <td>{item.waist}</td>
                        <td>{item.hip}</td>
                        <td>{item.length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ================================= */}
        {/* CART SECTION */}
        {/* ================================= */}

        <div className="sing-cart-section">
          {/* QUANTITY */}

          <div className="sing-quantity">
            <button type="button" onClick={decreaseQty}>
              -
            </button>

            <span>{qty}</span>

            <button type="button" onClick={increaseQty}>
              +
            </button>
          </div>

          {/* ADD TO CART */}

          <button
            type="button"
            className="sing-add-to-cart"
            onClick={handleAddToCart}
          >
            {isAdded ? "Go To Cart" : "Add To Cart"}
          </button>
        </div>

        <hr />

        {/* ==========================================
            DETAILS
        ========================================== */}

        <div className="sing-details-section">
          {/* DETAILS HEADING */}

          <button
            type="button"
            className="sing-details-header"
            onClick={() => setShowDetails((prev) => !prev)}
            aria-expanded={showDetails}
          >
            <span>Details</span>

            <span className={`sing-details-arrow ${showDetails ? "open" : ""}`}>
              +
            </span>
          </button>

          {/* DETAILS CONTENT */}

          {showDetails && (
            <div className="sing-product-info">
              <p>
                <b>Category:</b> {category}
              </p>

              <p>
                <b>Fabric:</b> {product.material || "-"}
              </p>

              <p>
                <b>Stock:</b> {product.quantity || "-"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
